import { esErrorApi } from '../api';

/**
 * Qué se le muestra a quien factura cuando la emisión falla de verdad (KAN-145).
 *
 * ⚠️ **El rechazo de ARCA (`Comprobante` con `resultado: RECHAZADO`) NO pasa
 * por acá.** Eso no es un `ErrorApi` — `emitirComprobante` lo devuelve como
 * dato, no lo lanza — y su texto sale directo de `errores`/`observaciones`,
 * que son la explicación de ARCA y no un código para traducir. Esto es
 * solo para lo que sí impidió llegar a preguntarle a ARCA.
 */

const GENERICO = 'No se pudo completar la emisión. Probá de nuevo.';

/** Qué se está leyendo cuando falla una consulta (KAN-146). */
export type Lectura = 'listado' | 'detalle' | 'resumen';

/**
 * Qué se muestra cuando falla una **lectura** de comprobantes (KAN-146).
 *
 * Está separado de {@link mensajeDeEmision} porque las dos pantallas dicen
 * cosas distintas ante el mismo código: un `sin_conexion` emitiendo significa
 * "no sabemos si ese comprobante existe, no recargues"; leyendo significa
 * apenas "volvé a intentar". Un texto compartido sería, en el mejor caso,
 * inútil en las dos.
 *
 * Se decide sobre el **código**, nunca sobre el `detalle` (ver la regla en
 * `api/errores.ts`).
 */
export function mensajeDeConsulta(error: unknown, lectura: Lectura): string {
  if (!esErrorApi(error)) {
    return 'No se pudieron traer los datos. Probá de nuevo.';
  }
  switch (error.codigo) {
    case 'no_encontrado':
      // Solo el detalle puede dar 404, y el id sale de la URL: lo más probable
      // es una dirección vieja o escrita a mano.
      return 'No existe un comprobante con ese id. Revisá el enlace: el id es el interno del comprobante, no su número (ese que se ve como 00001-00000015).';
    case 'tenant_no_autorizado':
    case 'prohibido':
      return 'Tu credencial no puede leer los comprobantes de ese contribuyente.';
    case 'request_invalida':
    case 'operacion_invalida':
      return 'Los filtros no son válidos. Revisá que la fecha "desde" no sea posterior a la de "hasta".';
    case 'no_autenticado':
      return 'Tu sesión ya no vale. Volvé a entrar.';
    case 'sin_conexion':
      // Deliberadamente tranquilizador, al revés que en la emisión: una lectura
      // que no llegó no dejó nada a medio hacer.
      return 'No se pudo contactar al servicio. No pasó nada: esto solo consulta, así que podés reintentar sin riesgo.';
    case 'error_interno':
      return 'El servicio tuvo un problema al consultar. Probá de nuevo en un momento.';
    default:
      return lectura === 'detalle'
        ? 'No se pudo traer el comprobante. Probá de nuevo.'
        : 'No se pudieron traer los comprobantes. Probá de nuevo.';
  }
}

export function mensajeDeEmision(error: unknown): string {
  if (!esErrorApi(error)) {
    return GENERICO;
  }
  switch (error.codigo) {
    case 'tenant_no_autorizado':
      return 'Tu credencial no está autorizada a emitir a nombre de ese tenant.';
    case 'no_encontrado':
      return 'Ese tenant ya no existe. Actualizá la lista.';
    case 'operacion_invalida':
      return 'Ese tenant no está habilitado para emitir ahora mismo (inactivo, o su delegación de ARCA no está activa).';
    case 'emision_en_curso':
      // Ojo: NO se reintenta solo (`mutations.retry: false`, ver
      // `FormularioDeEmision`). Hay que decirle a la persona que apriete
      // "Emitir" de nuevo, y que hacerlo es seguro: la clave de idempotencia
      // NO se renueva para este código, así que el reintento va con la misma
      // y ARCA no ve dos pedidos.
      return 'Ya hay una emisión en curso con este mismo pedido. Esperá unos segundos y volvé a apretar "Emitir": se reintenta con la misma operación, sin duplicar el comprobante.';
    case 'clave_idempotencia_reusada':
      // No debería pasar si `idempotencia.ts` se respeta; si pasa, es un bug
      // del cliente y no algo que la persona pueda corregir cambiando datos.
      return 'Hubo un problema interno con esta operación. Recargá la página antes de reintentar.';
    case 'punto_venta_no_habilitado':
      // El 11002 de ARCA. Nada se emitió y el backend libera la clave, así que
      // reintentar es seguro una vez corregido: por eso lo dice.
      return 'El punto de venta de ese tenant no está habilitado para Web Services en ARCA. No se emitió nada. Hay que habilitarlo en ARCA ("Puntos de Venta y Domicilios", sistema Web Services, no "Factura en Línea") o corregir el punto de venta del tenant; después podés volver a emitir.';
    case 'error_arca':
      // A diferencia del caso anterior, acá NO se sabe si ARCA llegó a autorizar.
      // Reintentar "por las dudas" es justo el doble comprobante que no se borra.
      return 'ARCA no pudo procesar el pedido y no se sabe si llegó a emitir el comprobante. No lo reintentes todavía: revisá en "Comprobantes" si figura, y si no, avisá a quien administra el servicio.';
    case 'request_invalida':
      return 'Revisá los campos marcados.';
    case 'no_autenticado':
      return 'Tu sesión ya no vale. Volvé a entrar.';
    case 'sin_conexion':
      return 'No se pudo contactar al servicio. Si apretaste "Emitir", NO se sabe si ese pedido llegó a procesarse: reintentá con el mismo botón, no recargues la página ni cambies los datos.';
    case 'error_interno':
      return 'El servicio tuvo un problema procesando la emisión. Antes de reintentar, esperá un momento: si el pedido llegó a reservarse, un reintento inmediato puede dar "emisión en curso" hasta que se resuelva.';
    default:
      return GENERICO;
  }
}
